import type {
  LoginDto,
  ConfirmIbanPaymentDto,
  InitiateCheckoutDto,
  CreateOrderDto,
  OverrideOrderStatusDto,
  CreateClientDto,
  UpdateClientDto,
  InviteUserDto,
  UpdateUserDto,
  SetUserStatusDto,
  OrderListQueryDto,
  ReassignClientsDto,
  ReassignClientsPreviewDto,
  PaginationQueryDto,
  CreateOfficeDto,
  UpdateOfficeDto,
  VatStatus,
  PermissionOverride,
  Capability,
  PermissionLevel,
  UserRole,
} from '@jalupro/shared';
import { http } from './client';
import type {
  LoginResult,
  Enroll2faStartView,
  MeView,
  Paginated,
  OrderSummaryView,
  OrderDetailView,
  ClientView,
  ProductView,
  PaymentView,
  UserView,
  OfficeView,
  IntegrationSettingView,
  ReassignPreviewView,
} from './types';

/**
 * Typed API surface, grouped by domain. Request bodies use the frozen
 * @jalupro/shared zod-DTO types; responses use the view models in ./types.
 *
 * These are the single place screen agents reach for data; see ./index for the
 * `useApi` / `useMutation` hooks that wrap loading + error state.
 */

type ListQuery = Partial<OrderListQueryDto & PaginationQueryDto>;

/**
 * PATCH /me body. Mirrors the API's `updateOwnProfileSchema` (fullName/office/phone),
 * but only the persisted fields are surfaced — `phone` has no User column server-side.
 */
export interface UpdateOwnProfileBody {
  fullName?: string;
  firstName?: string;
  lastName?: string;
  office?: string;
  phone?: string;
  /** Preferred UI language; persisted so the choice follows the user across devices. */
  language?: 'en' | 'es' | 'de';
}

/**
 * Optional filters for GET /agents/export.csv (same shape as the staff list query).
 * The index signature lets it satisfy the http client's `query` parameter type.
 */
export interface StaffExportQuery {
  role?: string;
  status?: string;
  search?: string;
  [key: string]: string | undefined;
}

export const api = {
  auth: {
    /** POST /auth/login — returns a challenge (TOTP / enrollment) or tokens. */
    login: (dto: LoginDto) =>
      http.post<LoginResult>('/auth/login', dto, { anonymous: true }),

    /** POST /auth/login with a recovery code instead of a TOTP code. */
    recoveryLogin: (body: { email: string; password: string; recoveryCode: string }) =>
      http.post<LoginResult>('/auth/recovery-login', body, { anonymous: true }),

    /** GET /auth/me — the authenticated principal. */
    me: () => http.get<MeView>('/auth/me'),

    /** POST /auth/logout — revokes the refresh token server-side. */
    logout: () => http.post<void>('/auth/logout'),

    /** POST /auth/2fa/enroll/start — begins first-login TOTP enrollment. */
    start2fa: (enrollmentToken: string) =>
      http.post<Enroll2faStartView>(
        '/auth/2fa/enroll/start',
        { enrollmentToken },
        { anonymous: true },
      ),

    /** POST /auth/2fa/enroll/confirm — confirm the 6-digit code to finish enrollment. */
    confirm2fa: (body: { enrollmentToken: string; totpCode: string }) =>
      http.post<LoginResult>('/auth/2fa/enroll/confirm', body, { anonymous: true }),

    /** POST /auth/first-login/set-password — set own password for a temp-password account. */
    setFirstLoginPassword: (body: { passwordChangeToken: string; newPassword: string }) =>
      http.post<LoginResult>('/auth/first-login/set-password', body, { anonymous: true }),

    /** POST /auth/2fa/setup/start — authenticated self-service: begin enabling 2FA. */
    setup2faStart: () => http.post<Enroll2faStartView>('/auth/2fa/setup/start'),

    /** POST /auth/2fa/setup/confirm — authenticated self-service: confirm + enable 2FA. */
    setup2faConfirm: (totpCode: string) =>
      http.post<{ ok: true }>('/auth/2fa/setup/confirm', { totpCode }),

    /** POST /auth/2fa/disable — authenticated self-service: disable 2FA (agents only). */
    disable2fa: (totpCode: string) =>
      http.post<{ ok: true }>('/auth/2fa/disable', { totpCode }),

    /** POST /auth/change-password. */
    changePassword: (body: { currentPassword: string; newPassword: string }) =>
      http.post<void>('/auth/change-password', body),

    /**
     * POST /auth/password-reset/request — send a password-reset email to {email}.
     * Public endpoint; always resolves 204 (never reveals whether the email exists).
     */
    requestPasswordReset: (email: string) =>
      http.post<void>('/auth/password-reset/request', { email }, { anonymous: true }),
  },

  /** Public runtime config (browser-safe settings served from the API env). */
  config: {
    get: () =>
      http.get<{ googleMapsApiKey: string | null }>('/config', { anonymous: true }),
  },

  orders: {
    list: (query: ListQuery = {}) =>
      http.get<Paginated<OrderSummaryView>>('/orders', { query }),
    get: (id: string) => http.get<OrderDetailView>(`/orders/${id}`),
    create: (dto: CreateOrderDto) => http.post<OrderDetailView>('/orders', dto),
    /** Admin/super-admin manual status override (reason recorded in audit log). */
    override: (id: string, dto: OverrideOrderStatusDto) =>
      http.patch<OrderDetailView>(`/orders/${id}/status`, dto),
    /** Archive an order (soft delete; admin/super-admin only). Restorable. */
    archive: (id: string) =>
      http.delete<{ id: string; archived: boolean }>(`/orders/${id}`),
    /** Restore a previously archived order (admin/super-admin only). */
    restore: (id: string) =>
      http.post<{ id: string; archived: boolean }>(`/orders/${id}/restore`, {}),
  },

  clients: {
    list: (query: ListQuery = {}) =>
      http.get<Paginated<ClientView>>('/clients', { query }),
    get: (id: string) => http.get<ClientView>(`/clients/${id}`),
    create: (dto: CreateClientDto) => http.post<ClientView>('/clients', dto),
    update: (id: string, dto: UpdateClientDto) =>
      http.patch<ClientView>(`/clients/${id}`, dto),
    /** Delete a client (Super Admin); hard-deletes when it has no orders, else soft-archives. */
    remove: (id: string) =>
      http.delete<{ id: string; archived: boolean }>(`/clients/${id}`),
    /** Agent: ask a super-admin to remove a client, with a reason (never deletes). */
    requestRemoval: (id: string, reason: string) =>
      http.post<{ id: string; requested: true }>(`/clients/${id}/request-removal`, { reason }),
    /** Bulk reassignment (Super Admin, Sales Manager): preview the selection, then apply it. */
    reassignPreview: (dto: ReassignClientsPreviewDto) =>
      http.post<ReassignPreviewView>('/clients/reassign/preview', dto),
    reassign: (dto: ReassignClientsDto) => http.post<{ count: number }>('/clients/reassign', dto),
    /** Duplicate pre-check before saving: other clients sharing the e-mail / phone (a warning). */
    checkDuplicates: (dto: { email?: string; phone?: string; excludeId?: string }) =>
      http.post<{
        emailCount: number;
        phoneCount: number;
        matches: Array<{ id: string; company: string; field: 'email' | 'phone' }>;
      }>('/clients/check-duplicates', dto),
    /** On-demand VIES VAT validation (form "Validate" button). `countryCode` is the
     *  billing-country fallback used when the VAT is entered without a prefix. */
    validateVat: (vatNumber: string, countryCode?: string) =>
      http.post<{
        status: VatStatus;
        name: string | null;
        address: string | null;
        normalized: string | null;
      }>('/clients/vat/validate', { vatNumber, countryCode }),
  },

  products: {
    /** Catalog comes from Holded (read-only); the API serves a cached list. */
    list: (query: ListQuery = {}) =>
      http.get<Paginated<ProductView>>('/products', { query }),
    /** Manual Holded catalog sync (ADMIN / SUPER_ADMIN only). Returns the tally. */
    sync: () =>
      http.post<{ created: number; updated: number; unchanged: number; total: number }>(
        '/products/sync',
      ),
  },

  payments: {
    list: (query: ListQuery = {}) =>
      http.get<Paginated<PaymentView>>('/payments', { query }),
    /** Admin/accountant manual IBAN confirmation. */
    confirmIban: (dto: ConfirmIbanPaymentDto) =>
      http.post<PaymentView>('/payments/confirm-iban', dto),
    /** Start a hosted Stripe/PayPal checkout; returns the redirect URL. */
    initiateCheckout: (dto: InitiateCheckoutDto) =>
      http.post<{ checkoutUrl: string }>('/payments/checkout', dto),
  },

  users: {
    /** Staff directory. Pass `excludeAgents` to hide AGENT rows (the Users screen
     *  manages non-agent roles; agents live under /admin/agents). */
    list: (query: ListQuery & { excludeAgents?: boolean } = {}) =>
      http.get<Paginated<UserView>>('/users', { query }),
    get: (id: string) => http.get<UserView>(`/users/${id}`),
    /** Create a user; returns the row plus the one-time temporary password to show. */
    invite: (dto: InviteUserDto) =>
      http.post<UserView & { temporaryPassword: string }>('/users/invite', dto),
    update: (id: string, dto: UpdateUserDto) =>
      http.patch<UserView>(`/users/${id}`, dto),
    setStatus: (id: string, dto: SetUserStatusDto) =>
      http.patch<UserView>(`/users/${id}/status`, dto),
    /** Admin sets/resets a managed user's password (revokes their sessions). */
    resetPassword: (id: string, newPassword: string) =>
      http.post<{ ok: true }>(`/users/${id}/password`, { newPassword }),
    /** Reset a user's TOTP 2FA — forces re-enrolment on next login. */
    resetTwoFactor: (id: string) =>
      http.post<{ ok: true }>(`/users/${id}/reset-2fa`),
    /**
     * DELETE /users/:id — soft-delete a managed user; their orders are reassigned.
     * Returns { deleted, reassignedOrders }. SUPER_ADMIN / ADMIN only (route-gated).
     */
    remove: (id: string) =>
      http.delete<{ deleted: true; reassignedOrders: number }>(`/users/${id}`),
  },

  /** Self-service profile for the logged-in principal (any role). */
  me: {
    /** GET /me — own profile (role/status/2FA flag). Returns the richer staff view. */
    get: () => http.get<MeView>('/me'),
    /**
     * PATCH /me — update own contact fields. The API's updateOwnProfileSchema accepts
     * { fullName?, office?, phone? }, but `phone` is not a User column and is silently
     * ignored server-side, so the client only sends the persisted fields.
     */
    update: (dto: UpdateOwnProfileBody) => http.patch<MeView>('/me', dto),
  },

  /** Agents directory (admin screen-8). Agents are users with role AGENT. */
  agents: {
    list: (query: ListQuery = {}) =>
      http.get<Paginated<UserView>>('/users', { query: { ...query, role: 'AGENT' } }),
    /**
     * GET /agents/export.csv — staff report as CSV. The endpoint returns
     * `text/csv`, which the http client surfaces as a plain string; callers wrap it
     * in a Blob for a client-side download (the Bearer token can't ride a plain
     * <a href>). SUPER_ADMIN / ADMIN only (route-gated).
     */
    exportCsv: (query: StaffExportQuery = {}) =>
      http.get<string>('/agents/export.csv', { query }),
    /**
     * GET /agents/:id/orders.csv — a single agent's orders as CSV (text/csv,
     * surfaced as a string; wrapped in a Blob for client-side download).
     */
    exportOrdersCsv: (id: string) =>
      http.get<string>(`/agents/${id}/orders.csv`),
  },

  /** System / integration settings (SUPER_ADMIN). Read-only in Phase 1. */
  settings: {
    integrations: () => http.get<IntegrationSettingView[]>('/settings/integrations'),
  },

  /**
   * Permission matrix (Area 1 §4.3): effective overrides for every signed-in user; the
   * full matrix + edits are Super Admin only (settings.manage).
   */
  permissions: {
    effective: () => http.get<PermissionOverride[]>('/permissions'),
    matrix: () => http.get<PermissionMatrixView>('/permissions/matrix'),
    save: (changes: PermissionOverride[]) => http.put<PermissionMatrixView>('/permissions', { changes }),
    reset: () => http.post<PermissionMatrixView>('/permissions/reset', {}),
  },

  /**
   * Offices ("Agente responsable"). GET is available to any authenticated user (to
   * populate pickers); create/update/delete are SUPER_ADMIN (route-gated).
   */
  offices: {
    list: () => http.get<OfficeView[]>('/offices'),
    create: (dto: CreateOfficeDto) => http.post<OfficeView>('/offices', dto),
    update: (id: string, dto: UpdateOfficeDto) =>
      http.patch<OfficeView>(`/offices/${id}`, dto),
    remove: (id: string) => http.delete<{ deleted: true }>(`/offices/${id}`),
  },
} as const;

export type Api = typeof api;

/** One cell of the permission matrix as served by GET /permissions/matrix. */
export interface PermissionMatrixCell {
  readonly capability: Capability;
  readonly role: UserRole;
  readonly level: PermissionLevel;
  readonly defaultLevel: PermissionLevel;
}

export interface PermissionMatrixView {
  readonly roles: readonly UserRole[];
  readonly capabilities: readonly Capability[];
  readonly cells: readonly PermissionMatrixCell[];
  readonly overrides: readonly PermissionOverride[];
}
